import { useCallback } from 'react'
import { queryClient } from 'src/config/request'
import { QueryKey } from '@tanstack/react-query'
import { PostProps } from '../usePosts'
import {
  FeedPages,
  OnPrependPostToCacheParams,
  OnDeletePostToCacheParams,
  OnUpdatePostCacheParams,
  OnUpdateCacheArtifactParams,
  OnGetCacheArtifactParams,
  OnGetPostFromCacheParams
} from './useManageFeedCache.types'

const useManageFeedCache = () => {
  const onGetCacheArtifact = <T>({
    queryKey
  }: OnGetCacheArtifactParams): [QueryKey, T | undefined] | undefined => {
    const [cacheArtifact] = queryClient.getQueriesData<T>(queryKey)
    const isValidCacheArtifact = Array.isArray(cacheArtifact) && cacheArtifact.length > 0

    if (!isValidCacheArtifact) {
      return undefined
    }

    return cacheArtifact
  }

  const onUpdateCacheArtifact = <T>({ queryKey, updatedData }: OnUpdateCacheArtifactParams<T>) => {
    const cacheArtifact = onGetCacheArtifact<T>({ queryKey })

    if (cacheArtifact) {
      const [, data] = cacheArtifact
      queryClient.setQueryData(queryKey, { ...data, ...updatedData })
    }
  }

  const onUpdatePostCache = useCallback(
    async <T>({ queryKey, postId, updatedData }: OnUpdatePostCacheParams<T>) => {
      const queriesData = queryClient.getQueriesData<FeedPages<PostProps>>({ queryKey })

      queriesData.forEach(([key, data]) => {
        if (!data?.pages?.length) return

        const pages = data.pages.map((page) => ({
          ...page,
          news: {
            ...page.news,
            data: page.news.data.map((post) =>
              post.id === postId ? { ...post, ...updatedData } : post
            )
          }
        }))

        queryClient.setQueryData(key, { ...data, pages })
      })

      if (Object.keys(updatedData).length) {
        queryClient.setQueryData([`getPost-${postId}`], updatedData)
      }
    },
    []
  )

  const onDeletePostToCache = useCallback(({ queryKey, postId }: OnDeletePostToCacheParams) => {
    const queriesData = queryClient.getQueriesData<FeedPages<PostProps>>({ queryKey })

    queriesData.forEach(([key, data]) => {
      if (!data?.pages?.length) return

      const pages = data.pages.map((page) => ({
        ...page,
        news: {
          ...page.news,
          data: page.news.data.filter((post) => post.id !== postId)
        }
      }))

      queryClient.setQueryData(key, { ...data, pages })
    })
  }, [])

  const onPrependPostToCache = useCallback(({ queryKey, post }: OnPrependPostToCacheParams) => {
    const queriesData = queryClient.getQueriesData<FeedPages<PostProps>>({ queryKey })

    queriesData.forEach(([key, data]) => {
      if (!data?.pages?.length) return

      const pages = data.pages.map((page, index) => {
        if (index !== 0) return page

        return {
          ...page,
          news: {
            data: [post, ...page.news.data]
          }
        }
      })

      queryClient.setQueryData(key, { ...data, pages })
    })
  }, [])

  const onGetPostFromCache = useCallback(({ queryKey, postId }: OnGetPostFromCacheParams) => {
    const cacheArtifact = onGetCacheArtifact<FeedPages<PostProps>>({ queryKey })

    if (!cacheArtifact) return

    const [, data] = cacheArtifact

    return data?.pages.reduce<PostProps | undefined>((acc, page) => {
      return page.news.data.find((post) => post.id === postId) || acc
    }, undefined)
  }, [])

  return {
    onGetCacheArtifact,
    onUpdateCacheArtifact,
    onUpdatePostCache,
    onDeletePostToCache,
    onPrependPostToCache,
    onGetPostFromCache
  }
}

export default useManageFeedCache
